import {
    AfterViewInit, Component, EventEmitter, OnInit, Output, Input, Type as AngularCoreType, ViewChild, SimpleChanges,
    ComponentFactoryResolver,
    ElementRef,
    Renderer2
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatTable } from '@angular/material/table';
import { TooltipPosition } from '@angular/material/tooltip';
import { CurrentUserService } from '../../../../security/current-user.service';
import { AddContactData, AddContactDialog } from '../../../../shared/dialogs/add-contact/add-contact.dialog';
import { ContactInfo, ContactsFormFieldData, IContactInfo } from '../../../models/contacts/contacts-form-field.model';
import { FormFieldPropertyEnum } from '../../../models/form-builder/form-field-property-enum.model';
import { FormField } from '../../../models/form-builder/form-field.model';
import { PickerItem } from '../../../models/picker-item.model';
import { FormFieldBaseComponent } from '../form-field-base/form-field-base.component';
import { ItemTypeEnum } from '../../../enums/item-type.enum';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { EmailService } from '../../../services/email.service';
import { SafeUrl } from '@angular/platform-browser';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../../dialogs/confirmation/confirmation-dialog.component';
import { ConfirmationDialogEnums } from '../../../enums/confirmation-dialog.enum';
import { GridFormFieldComponent } from '../grid-form-field/grid-form-field.component';
import { GridFlavorEnum, GridFlavorModifiersEnum } from '../../../enums/grid.enums';
import { CachedFormFieldService } from '../../../services/form-field-with-cache.service';
import { CurrentSiteService } from '../../../services/current-site.service';
import { DateAdapter } from '@angular/material/core';
import { FieldDefinitionService } from '../../../services/field-definition.service';
import { FormFieldTypeAndNameService } from '../../../services/form-field-type-and-name.service';
import { GridFieldEditorComponent } from '../grid-field-editor/grid-field-editor.component';
import { GridFieldDesignerComponent } from '../grid-field-designer/grid-field-designer.component';
import { CheckboxFieldType, ShortTextFieldType } from '../../../models/form-builder/form-field-types';
import { FormFieldConstraintLiaisonManager } from '../../form-builder/properties/form-field-properties/constraints/field-constraint-liaison-manager';
import { ComponentAndFormField } from '../../../models/grid/component-and-form-field-model';
import { FormFieldConfigurationSaveReason } from '../../../enums/form-field-save-config-reason.enum';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { MatMenuTrigger } from '@angular/material/menu';
import { GridCellContextMenuInfo } from '../../../models/grid/cell-context-menu-info.model';
import { GridConfig } from '../../../models/grid/grid-config.model';
import { FormModeEnum } from '../../../enums/form-mode.enum';
import { ContactsGridNotificationTemplateAssociations } from '../../../models/notifications/contacts-grid-notification-template-association.model';
import { NotificationService } from '../../../services/notification.service';
import { EmailTemplateSelectionDialog, EmailTemplateSelectionDialogManager } from '../../../dialogs/email-template-selection/email-template-selection.dialog';
import { EmailTemplateContentDialog, EmailTemplateContentDialogModel } from '../../../dialogs/email-template-content/email-template-content.dialog';

// A KEY THING TO KNOW about this component is that it displays and creates PLAIN OLD GridRows in the GridRow table
// For an overview of how the Contacts flavor of Grid works: https://community-dc.max.gov/x/bgRGmg
@Component({
    selector: 'app-contacts-form-field',
    templateUrl: './contacts-form-field.component.html',
    styleUrls: ['./contacts-form-field.component.scss', '../form-fields.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: ContactsFormFieldComponent,
            multi: true
        }
    ],
    standalone: false
})
export class ContactsFormFieldComponent extends FormFieldBaseComponent implements OnInit, AfterViewInit {
    // Properties.
    // Note:  several properties are implemented in my base class.
    @Output() onInit = new EventEmitter();
    @Output() onEdit = new EventEmitter();
    // Emitted by design mode changes like adding/deleting a column
    @Output() designChange = new EventEmitter();

    @Input() flavor: GridFlavorEnum = GridFlavorEnum.ContactsGrid;
    @Input() flavorModifier: GridFlavorModifiersEnum;

    private numDisplayRows: number = 1;
    private contactsData: ContactsFormFieldData = new ContactsFormFieldData();
    private selfAddedFlag: boolean = false;

    @ViewChild(MatTable) table: MatTable<IContactInfo>;
    @ViewChild(MatMenuTrigger) gridCellContextMenu: MatMenuTrigger;

    private readonly formFieldProperties: string[] =
        [
            FormFieldPropertyEnum.REQUIRED,
            FormFieldPropertyEnum.NAME,
            FormFieldPropertyEnum.FIELD_GROUP,
            FormFieldPropertyEnum.DISPLAY_NAME,
            FormFieldPropertyEnum.BLANK_VALUE,
            FormFieldPropertyEnum.HELP_TEXT,
            FormFieldPropertyEnum.INSTRUCTIONS_TEXT,
            FormFieldPropertyEnum.NUMBER_OF_DISPLAY_ROWS,
            FormFieldPropertyEnum.SHOW_CONTACTS_PROPERTIES,
            FormFieldPropertyEnum.CUSTOM_EMAIL_TEMPLATE_SELECTION
        ];

    private displayedColumns: string[] = [];
    // Gets set to true if the Contacts Grid is configured with one or more columns beyond the standard (Name, Email etc) ones
    private hasExtraFields: boolean;

    //VNEXT-614: KLW - Adding property getter to determine if control should be disabled
    public get Disabled() {
        return this.readOnly;
    }

    private readonly numDisplayRowsValues: string[] = ['1', '2', '3', '4', '5'];

    // This is the component that the contacts component uses to render itself as a Kendo Grid
    private wrappedGridFormFieldComponent: GridFormFieldComponent;

    // Constructor.
    public constructor(private resolver: ComponentFactoryResolver,
        private hostElement: ElementRef,
        private renderer: Renderer2,
        private cachedFormFieldConstraintsService: CachedFormFieldService,
        private currentSiteService: CurrentSiteService,
        private currentUserService: CurrentUserService,
        private dateAdapter: DateAdapter<Date>,
        private fieldDefinitionService: FieldDefinitionService,
        private formFieldTypeAndNameService: FormFieldTypeAndNameService,
        private emailService: EmailService, //VNEXT-811: KLW - Needed for emailing contacts
        private dialog: MatDialog,
        private notificationService: NotificationService)
    { // Not yet using:  rendererParam: Renderer2
        super();
    }

    // Life cycle methods.
    public ngOnInit(): void {


        // START ----------------------------- NEW ngOnInit() STUFF FOR CONTACTS GRID ---------------------------
        this.wrappedGridFormFieldComponent = new GridFormFieldComponent(
            this.resolver,
            this.hostElement,
            this.renderer,
            null,
            this.dialog,
            this.cachedFormFieldConstraintsService,
            this.currentSiteService,
            this.currentUserService,
            this.dateAdapter,
            this.fieldDefinitionService,
            this.formFieldTypeAndNameService
        );

        // Set Inputs 
        this.wrappedGridFormFieldComponent.formField = this.formField;
        this.wrappedGridFormFieldComponent.flavor = this.ContactsGridFlavor;
        this.wrappedGridFormFieldComponent.mode = this.mode;

        this.wrappedGridFormFieldComponent.ngOnInit();

        // EVENT HANDLERS ~ because GridFieldDesigner and GridFieldEditor events are emitted through GridFormFieldComponent, we have to
        // subscribe to them here, rather than in the HTML
        this.wrappedGridFormFieldComponent.onEdit.subscribe(data => {
            this.onEdit.emit(data);
        });
        this.wrappedGridFormFieldComponent.designChange.subscribe(saveDesignChange => {
            this.designChange.emit(saveDesignChange);
        });
        // Make sure the component gets protected from accidental data loss - see  https://community-dc.max.gov/x/MI4ynw for details
        this.wrappedGridFormFieldComponent.flaggedAsDirty.subscribe(() => {
            this.FormInstanceElement.UserUpdatedData = true;
            this.FormInstanceElement.gridRowsValueHasChanged = true;
        });
        this.wrappedGridFormFieldComponent.flaggedAsClean.subscribe(() => {
            this.FormInstanceElement.UserUpdatedData = false;
            this.FormInstanceElement.gridRowsValueHasChanged = false;
        });
        // Subscribe to and pass along the wrapped grid's formFieldValidated event so it can handled by the parent component (FormFieldWrapper)
        this.wrappedGridFormFieldComponent.formFieldValidated.subscribe(status => {
            this.formFieldValidated.emit(status);
        });
        // END ------------------------------ NEW ngOnInit() STUFF FOR CONTACTS GRID -------------------------------

        // Extra fields are identified by not having "donotedit" in their internal name. This is because all of the
        // standard fields (Name, Email etc) are given names with this suffix, such as contactnamedonotedit, contactemaildonotedit etc
        this.hasExtraFields = (this.formField.childFormFields.filter(x => { return x.name.indexOf('donotedit') == -1 }).length > 0);


        this.propertyUpdated(this.FormField, FormFieldPropertyEnum.NUMBER_OF_DISPLAY_ROWS);

        if (this.Mode === 'design') {
            if ((this.FormField.contactFieldNames == null) || (this.FormField.contactFieldNames.trim() == ''))
                this.FormField.contactFieldNames = 'name|email|phone';

            this.createExampleContacts();
        } else if ((this.Mode === 'preview') || (this.Mode === 'instance')) {
        }

        // Derived columns to display.
        this.deriveDisplayedColumns();

        // Emit 'onInit' to my parent component.
        let hshProperties = this.getProperties();
        this.onInit.emit(hshProperties);

        //**Assign the value for the gridConfig
        this.gridConfig = this.wrappedGridFormFieldComponent.GridConfig;
    }

    // =================================== NEW STUFF FOR VNEXT-1100 ~ UPDATED CONTACTS GRID =====================================

    @ViewChild(GridFieldDesignerComponent, { read: GridFieldDesignerComponent }) gridFieldDesignerComponent: GridFieldDesignerComponent;
    @ViewChild(GridFieldEditorComponent, { read: GridFieldEditorComponent }) gridFieldEditorComponent: GridFieldEditorComponent;
    private contactsInWrappedKendoGrid: GridDataResult = null;
    private gridConfig: GridConfig = null;

    public get WrappedGridFormFieldComponent(): GridFormFieldComponent {
        return this.wrappedGridFormFieldComponent;
    }

    public get ContextMenuPositionX(): string {
        let menuData: GridCellContextMenuInfo = (this.gridCellContextMenu && this.gridCellContextMenu.menuData ? <GridCellContextMenuInfo>this.gridCellContextMenu.menuData : null);
        return (menuData ? menuData.contextMenuXPos : '');
    }

    public get ContextMenuPositionY(): string {
        let menuData: GridCellContextMenuInfo = (this.gridCellContextMenu && this.gridCellContextMenu.menuData ? <GridCellContextMenuInfo>this.gridCellContextMenu.menuData : null);
        return (menuData ? menuData.contextMenuYPos : '');
    }

    // Add a new contact with dialog
    public openNewContactDialog(addMepickerItem: PickerItem = null) {
        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.width = '1100px';
        dialogConfig.height = this.hasExtraFields ? '800px' : '600px';
        dialogConfig.data = { fields: this.formField.childFormFields, formInstance: this.formInstance, pickerItem: addMepickerItem };

        let dialogRef = this.dialog.open(AddContactDialog, dialogConfig);

        dialogRef.afterClosed().subscribe((data: AddContactData) => {
            this.addContactAsGridRow(data);
        });
    }

    public addMeButtonClicked(): void {
        const u = this.currentUserService.user;
        let addMePickerItem = new PickerItem({
            key: u.maxId,
            displayName: u.displayName,
            secondaryDisplayName: u.emailAddress,
            isUser: true,
            isActive: true,
            phone: u.phone,
            agencyShortName: u.agencyName
        });

        if (this.hasExtraFields) {
            // open in the dialog so that any validations on the extra fields can be enforced
            this.openNewContactDialog(addMePickerItem);
        } else {
            let contact = new AddContactData(addMePickerItem, []);
            this.addContactAsGridRow(contact);
        }
    }

    // After the call to KendoGridService to create the Contact, rebind() gets called on the gridFieldEditorComponent which
    // causes the grid to be reloaded. (It's just the easiest way to display newly added contacts) and after that we
    // subscribe to the gridFieldEditorComponent's gridEvent event and handle it if it's of type 'gridLoaded'
    // NOTE: What gets created in the backend when a new contact is created is a plain old GridRow
    private addContactAsGridRow(data: AddContactData) {
        if (data) {
            let contact = new ContactInfo(data.selectedUser, data.extraFieldFormInstanceElements, data.isPrimaryConact);

            this.gridFieldEditorComponent.KendoGridService.addContactAsGridRow(this.FormInstanceElement.id, this.formInstance.id, this.formField.id, contact).then(x => {
                this.gridFieldEditorComponent.RowCount += 1;
                this.WrappedGridFormFieldComponent.CheckFieldForValidation(); // adding the contact may change the field's validity status
                this.gridFieldEditorComponent.rebind();

                // after the rebind, wait for the gridLoaded event and handle it by flagging the underlying
                // FormInstanceElement as having been updated so the server knows to save it
                this.gridFieldEditorComponent.gridEvent.subscribe(event => {
                    // PJH Look here for possible place to fix false "you have unsaved changes" messages
                    if (event.type == 'gridLoaded') {
                        this.gridFieldEditorComponent.flagElementAsUpdated();
                    }
                });
            });
        }
    }

    // The way this flavor of Grid works is that when you create a new Contacts field you get a regular grid but it gets auto-populated with
    // five standard contact specific columns. Additional columns can be added if desired.
    public handleGridFieldFieldDesignerInit() {
        // Add the standard contacts fields if the Field is new
        if (this.wrappedGridFormFieldComponent.formField.id == 0) {
            this.wrappedGridFormFieldComponent.GridConfig.addGridColumn(ShortTextFieldType, ContactInfo.FIELD_INTERNAL_NAME_CONTACT_NAME, ContactInfo.FIELD_DISPLAY_NAME_CONTACT_NAME, null, true);
            this.wrappedGridFormFieldComponent.GridConfig.addGridColumn(ShortTextFieldType, ContactInfo.FIELD_INTERNAL_NAME_CONTACT_EMAIL, ContactInfo.FIELD_DISPLAY_NAME_CONTACT_EMAIL, ContactInfo.FIELD_CONSTARINT_NAME_CONTACT_EMAIL);
            this.wrappedGridFormFieldComponent.GridConfig.addGridColumn(ShortTextFieldType, ContactInfo.FIELD_INTERNAL_NAME_CONTACT_PHONE, ContactInfo.FIELD_DISPLAY_NAME_CONTACT_PHONE, ContactInfo.FIELD_CONSTRAINT_NAME_CONTACT_PHONE);
            this.wrappedGridFormFieldComponent.GridConfig.addGridColumn(ShortTextFieldType, ContactInfo.FIELD_INTERNAL_NAME_CONTACT_AGENCY, ContactInfo.FIELD_DISPLAY_NAME_CONTACT_AGENCY);
            this.wrappedGridFormFieldComponent.GridConfig.addGridColumn(CheckboxFieldType, ContactInfo.FIELD_INTERNAL_NAME_IS_PRIMARY_CONTACT, ContactInfo.FIELD_DISPLAY_NAME_IS_PRIMARY_CONTACT);
        }
    }

    // PJH - 1/23/2026 - VNEXT-2184 - override of base class method to enable validation of Grid's required status
    public CheckFieldForValidation() {
        const statusChangeName = this.FieldHasValidationError ? FormFieldPropertyEnum.INVALID : FormFieldPropertyEnum.VALID;
        if (this.mode != FormModeEnum.DESIGN) {
            this.emitValidation(statusChangeName);
        }
    }

    // PJH - 1/23/2026 - VNEXT-2184 - override of base class method to enable validation of Grid's required status
    public get FieldHasValidationError() {
        return this.FormField.required && !this.gridFieldEditorComponent.HasRows;
    }

    // PJH - 1/23/2026 - VNEXT-2184 - override of base class method to enable validation of Grid's required status
    public getValidationErrors(revealValidationErrors: boolean = false): string[] {
        if (this.FormField.required && !this.gridFieldEditorComponent.HasRows) {
            this.fieldHasValidationError = true;
            return [`${this.FormField.displayName || this.FormField.name} is required but has no rows`];
        } else {
            this.fieldHasValidationError = false;
            return [];
        }
    }

    public handleEditorGridValidation(event: any) {
        this.wrappedGridFormFieldComponent.mainGridValidation.emit(event);
    }

    public handleConditionalLogicColumnDefsFilter(event: any) {
        //console.log('AttachmentCOmp.handleConditionalLogicColumnDefsFilter($event)...');
    }
    public handleEditFieldProperty(event: any) {
        console.log('handleEditFieldProperty');
        console.log(event);
    }

    public saveConfiguration(form: any, reasonForSave: FormFieldConfigurationSaveReason): void {
        // Cache form fields during the save configuration operation.
        // TODO: should I cache the rows here in Attachments Component instead? OR is it best to delegate to Wrapped Grid
        this.wrappedGridFormFieldComponent.GridConfig.CachedGridColumnDefs = this.wrappedGridFormFieldComponent.GridConfig.ColumnDefs;

        // Call form fields' saveConfiguration() method.
        let allComponentsAndFormFields: ComponentAndFormField[] = this.wrappedGridFormFieldComponent.RuntimeMetadata.AllComponents;
        for (let iComponent: number = 0; iComponent < allComponentsAndFormFields.length; iComponent++) {
            let component: FormFieldBaseComponent = allComponentsAndFormFields[iComponent].Component;

            component.saveConfiguration(form, reasonForSave);
        }

        // Save my column definitions as JSON text.
        let currentSite = this.currentSiteService.Site;
        this.FormField.childFormFields = [];

        for (let iCol: number = 0; iCol < this.wrappedGridFormFieldComponent.GridConfig.ColumnCount; iCol++) {
            let gridColDef: FormField = this.wrappedGridFormFieldComponent.GridConfig.getColumnDef(iCol);

            let childFormField: FormField = new FormField();

            childFormField.assignFrom(gridColDef);
            if (childFormField.id < 0)
                childFormField.id = 0;
            FormFieldConstraintLiaisonManager.synchronousUpdateFormFieldWithLatestConstraintValues(this.cachedFormFieldConstraintsService, currentSite.id, childFormField);

            this.FormField.childFormFields.push(childFormField);
        }

        return;
    }

    //VNEXT-951: KLW - Changes to email a contact list
    public getEmailAllUrl(): string {
        let retVal: string = '';
        if (!this.checkContactsAdded()) {
            alert("No contacts have been added to the grid yet");
            return null;
        } else {
            // Try to find the child form field Id that corresponds to the contacts grid's email address field.
            const emailColumnDef: FormField = this.FormField.childFormFields != null ? this.FormField.childFormFields.find(ff => ff.name == ContactInfo.FIELD_INTERNAL_NAME_CONTACT_EMAIL) : null;

            if (emailColumnDef != null) {
                // 03-16-2026 note:  need to get the email value based on the child form field's Id, not its internal name.
                //var emails = this.contactsInWrappedKendoGrid.data.map(x => { return x[ContactInfo.FIELD_INTERNAL_NAME_CONTACT_EMAIL] });
                let emails = this.contactsInWrappedKendoGrid.data.map(x => { return x[emailColumnDef.id] });
                retVal = this.emailService.composeAllEmailUrl(emails, this.formInstanceName, window.location.href);
            }
        }

        return retVal;
    }

    // Called from Form Builder when properties drawer is closed
    public resetFormField(formField: FormField): void {
        this.wrappedGridFormFieldComponent.resetFormField(formField);
    }

    // ================================ END NEW STUFF FOR VNEXT-1100 ~ UPDATED CONTACTS GRID ====================================

    public ngAfterViewInit(): void {
        // Can see @ViewChild() values when this method is called.
        //**Set the context menu in this event since this event guarantees that the view template has been initialized
        this.wrappedGridFormFieldComponent.gridCellContextMenu = this.gridCellContextMenu;

    }

    // Methods called by my HTML code.
    public columnIsSticky(colName: string): boolean {
        // Note:  this does not need to be a hard code value, but it is.
        return true;
    }

    public get ContactsGridFlavor(): GridFlavorEnum {
        return GridFlavorEnum.ContactsGrid;
    }

    public get HeaderRowIsSticky(): boolean {
        return true;
    }

    public get NumDisplayRows(): number {
        return this.numDisplayRows;
    }

    public getContactTableCssClasses(isDiv: boolean): string {
        //VNEXT-951: KLW - Set default number of contact rows to 5
        let cssClasses: string =
            (isDiv ?
                'mat-table-div contacts-mat-table-div-5-row' :
                'mat-elevation-z4 contacts-mat-table contacts-mat-table-5-row');

        if (this.FormField.displayRows != null) {
            switch (this.FormField.displayRows) {
                case '2':
                case '3':
                case '4':
                case '5':
                    cssClasses =
                        (isDiv ?
                            `mat-table-div contacts-mat-table-div-${this.FormField.displayRows}-rows` :
                            `mat-elevation-z4 contacts-mat-table contacts-mat-table-${this.FormField.displayRows}-rows`);
                    break;

                default:
                    break;
            }
        }

        return cssClasses;
    }

    public get ContactsListing(): IContactInfo[] {
        return this.contactsData.contacts;
    }

    public get DisplayedColumns(): string[] {
        return this.displayedColumns;
    }
    public getColumnStyle(columnName: string): string {
        let style: string = 'font-weight: 900;';

        if (columnName == 'action')
            style += 'width: 15%;';
        else {
            let numDataCols: number = this.displayedColumns.length - 1;
            if (numDataCols == 1)
                style += 'width: 85%;';
            else if (numDataCols == 2) {
                if (this.displayedColumns.includes('name')) {
                    if (columnName == 'name')
                        style += 'width: 55%;';
                    else
                        style += 'width: 30%;';
                } else
                    style += 'width: 42.5%;';
            } else if (numDataCols == 3) {
                if (columnName == 'name')
                    style += 'width: 45%;';
                else
                    style += 'width: 20%;';
            }
        }

        return style;
    }

    public get AddContactLabel(): string {
        return 'Add Contact';
    }
    public get AddContactButtonTitle(): string {
        return 'Add Contact';
    }
    public get AddContactButtonAdditionalStyle(): string {
        return '';
    }
    public get AddContactButtonFlyoverText(): string {
        return 'Add a person to the contacts listing';
    }
    public get AddMeButtonTitle(): string {
        return 'Add Me';
    }
    public get AddMeButtonCanEnable(): boolean {
        return ((this.Mode != 'design') && (!this.selfAddedFlag));
    }
    public get AddMeButtonFlyoverText(): string {
        return 'Add yourself to the contacts listing'
    }

    public get PickerInputDisabled(): boolean {
        return (this.mode === 'design');
    }

    public get TooltipPosition(): TooltipPosition {
        let position: TooltipPosition = 'below';

        return (position);
    }

    public get EnableContactEditing(): boolean {
        // Note:  check with Shaun to see if this needs to be configurable.
        return true;
    }
    public getEditContactTooltip(element: IContactInfo): string {
        return null; // (element.editingEnabled ? 'End editing this contact' : 'Edit this contact');
    }
    public get EditContactButtonDisabled(): boolean {
        return (this.Mode === 'design');
    }

    public get TrashCanTooltip(): string {
        return 'Delete this contact';
    }
    public get TrashCanButtonDisabled(): boolean {
        return (this.mode === 'design');
    }

    //VNEXT-811: KLW - Needed for emailing contacts
    public get EnableEmailEditing(): boolean {
        // Note:  check with Shaun to see if this needs to be configurable.
        return true;
    }
    public get EmailToolTip(): string {
        return 'Email this contact';
    }
    public get EmailButtonDisabled(): boolean {
        return (this.Mode === 'design');
    }
    //VNEXT-951: KLW - Changes to email a contact list
    public get EmailAllButtonDisabled(): boolean {
        let retVal: boolean = false;

        if ((this.Mode === 'design')) {
            retVal = true;
        }

        if (this.ContactsListing) {
            if (this.ContactsListing.length <= 0) {
                retVal = true;
            }
        }
        else {
            retVal = true;
        }

        return retVal;
    }

    public get EmailAllToolTip(): string {
        return 'Email all contacts in the list';
    }

    public get AutomatedNotificationToolTip(): string {
        return 'Send an update notification email to everyone currently in the grid';
    }

    public get ActivityListIcon(): string {
        return ItemTypeEnum.ICON;
    }

    //VNEXT-811: KLW - Changes to email a contact
    public showEmailDialog() {
        let url = this.getEmailAllUrl();
        if (!url) {
            return;
        }

        /*
        this.FormInstanceElement.BypassSitePopUpWarning = true;

        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: new ConfirmationDialogModel(`Email Created`, `An email has been created using your email client and it should display shortly`, { showOk: true, hideNo: true, dialogType: ConfirmationDialogEnums.EMAIL })
        });

        dialogRef.afterOpened().subscribe(() => {
            this.FormInstanceElement.BypassSitePopUpWarning = false;
        });
        dialogRef.afterClosed().subscribe(() => {
            this.FormInstanceElement.BypassSitePopUpWarning = false;
        });
        */
        this.showEmailCreatedDialogue();

        // Open the email URL to get the browser to interact with the user's preferred email client.
        window.open(url, '_blank');
    }

    private showEmailCreatedDialogue(): void {
        this.FormInstanceElement.BypassSitePopUpWarning = true;

        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: new ConfirmationDialogModel(`Email Created`, `An email has been created using your email client and it should display shortly`, { showOk: true, hideNo: true, dialogType: ConfirmationDialogEnums.EMAIL })
        });

        dialogRef.afterOpened().subscribe(() => {
            this.FormInstanceElement.BypassSitePopUpWarning = false;
        });
        dialogRef.afterClosed().subscribe(() => {
            this.FormInstanceElement.BypassSitePopUpWarning = false;
        });
    }

    public generateClientSideEmail(): void {
        // Do we have any contacts?
        if (!this.checkContactsAdded()) {
            alert("No contacts have been added to the grid yet");
            return;
        }

        // See if there are any custom email templates associated with this contacts field.
        this.notificationService.getContactsTemplateNotificationTemplateAssociations(this.FormField.id, this.formInstance.id).then(response => {
            // Do we have custom templates?
            if ((response.formFieldNotificationTemplateAssociations != null) && (response.formFieldNotificationTemplateAssociations.length > 0)) {
                // Call a new method to handle this use case.
                this.handleCustomEmailTemplates(response);
            } else {
                // Simply process the original email dialogue.
                this.showEmailDialog();
            }
        });
    }

    private handleCustomEmailTemplates(response: ContactsGridNotificationTemplateAssociations): void {
        if (response.formFieldNotificationTemplateAssociations.length > 1) {
            // Need to select an email template.
            const dialogRef = new EmailTemplateSelectionDialogManager(response).openDialogUsing(this.dialog);

            dialogRef.afterClosed().subscribe(result => {
                // If the user selected a template, request an email generated with the template.
                if (result != null) {
                    // Request an email generated with the template.
                    //this.requestEmailUsingCustomTempalte(result.selectedFormFieldNotificationTemplateAssociation.notificationTemplateId);
                    this.requestEmailUsingCustomTemplate(result.notificationTemplateId);
                }
            });
        } else if (response.formFieldNotificationTemplateAssociations.length == 1) {
            // Generate an email using the only email template.
            this.requestEmailUsingCustomTemplate(response.formFieldNotificationTemplateAssociations[0].notificationTemplateId);
        }
    }

    private requestEmailUsingCustomTemplate(notificationTemplateId: number): void {
        // Ask the server to generate this email.
        this.notificationService.generateFormInstanceEmailUsingTemplate(this.FormInstanceElement.formInstanceId, notificationTemplateId).then(response => {
            // Generate an email URL and then display it in a tab (that will immediately go away).
            const emailColumnDef: FormField = this.FormField.childFormFields != null ? this.FormField.childFormFields.find(ff => ff.name == ContactInfo.FIELD_INTERNAL_NAME_CONTACT_EMAIL) : null;

            if (emailColumnDef != null) {
                // Let the user know that an email is being created.
                //this.showEmailCreatedDialogue();
                const dialogData = new EmailTemplateContentDialogModel(`Email Created`, response.bodyText);
                let dialogRef = this.dialog.open(EmailTemplateContentDialog, {
                    //data: new EmailTemplateContentDialogModel(`Email Created`, `An email has been created using your email client and it should display shortly`) //, { showOk: true, hideNo: true, dialogType: ConfirmationDialogEnums.EMAIL })
                    data: dialogData
                });

                // 03-16-2026 note:  need to get the email value based on the child form field's Id, not its internal name.
                let emailAddresses = this.contactsInWrappedKendoGrid.data.map(x => { return x[emailColumnDef.id] });
                const url = this.emailService.composeCustomEmailUrl(emailAddresses, response.subjectText, response.bodyText);

                window.open(url, '_blank');
            }
        });
    }

    public automatedNotificationDialog() { //VNEXT-1626
        if (!this.checkContactsAdded()) {
            alert("No contacts have been added to the grid yet");
            return;
        };

        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: new ConfirmationDialogModel(`Send Automated Email`,
                `All contacts will be sent a notification email`,
                { showOk: true, hideNo: false, dialogType: ConfirmationDialogEnums.EMAIL })
        });

        dialogRef.afterClosed().subscribe(response => {
            let contactsGridId = this.FormInstanceElement.id;
            this.emailService.SendContactsMail(contactsGridId).then(response => {

            });
        });
    }

    public checkContactsAdded(): boolean {
        /***Set the contacts in kendogrd value when its value is null and when it's length < what's in the db which means it's been changed*/
        if ((this.contactsInWrappedKendoGrid == null && this.gridFieldEditorComponent?.Data != null) ||
            (this.contactsInWrappedKendoGrid != null && this.contactsInWrappedKendoGrid.data.length != this.gridFieldEditorComponent?.Data.data.length))
        {
            this.contactsInWrappedKendoGrid = this.gridFieldEditorComponent.Data;
        }

        return this.contactsInWrappedKendoGrid?.data?.length > 0;
    }

    // End methods called by my HTML code.

    // Override base class methods.
    public getProperties(): any {
        let hshProperties = {
            component: this,
            formField: this.FormField,
            properties: this.formFieldProperties,
            displayRowsValues: this.numDisplayRowsValues,
            propertyUpdateRequired: true
        };

        return (hshProperties);
    }

    public getFormFieldClass(): AngularCoreType<any> {
        return (ContactsFormFieldComponent);
    }

    public hasNumericData(): boolean {
        return false;
    }

    // Initializes/reinitializes the component and its FormControl (formerly named writeValueTriggered)
    protected initalizeComponentFormControl(): void {
        if (this.IsInPreviewOrInstanceMode) {
            if (this.FormInstanceElement && this.FormInstanceElement.textValue) {
                this.contactsData = ContactsFormFieldData.deserializeData(this.FormInstanceElement);
            }
            if (this.gridFieldEditorComponent != null) {
                this.gridFieldEditorComponent.parentReceivedFormInstanceElement(this.FormInstanceElement);
            }
            this.checkSelfAdded();
        }

        return;
    }

    public propertyUpdated(formField: FormField, propertyName: string): void {
        if (propertyName == FormFieldPropertyEnum.SHOW_CONTACTS_PROPERTIES) {
            this.deriveDisplayedColumns();
        } else if (propertyName == FormFieldPropertyEnum.NUMBER_OF_DISPLAY_ROWS) {
            let numRows: number = parseInt(this.FormField.displayRows);
            if (!Number.isNaN(numRows)) {
                this.numDisplayRows = numRows;

                if ((this.Mode === 'design') && (numRows != this.contactsData.contacts.length))
                    this.createExampleContacts();
            }
        }
    }

    // Handle controle events.
    public addContactButtonClicked(eventData: PickerItem): void {
    }

    public deleteContact(element: IContactInfo): void {
        this.contactsData.contacts = this.contactsData.contacts.filter(c => c.userId != element.userId);
        this.table.renderRows();

        this.packageDataAndUpdateFlags();
    }

    public editContact(element: IContactInfo): void {
        //element.editingEnabled = (!element.editingEnabled);
    }

    public handleModelChange(element: IContactInfo, fieldName: string): void {
        // Note:  this method is only used during testing/troubleshooting.
        this.packageDataAndUpdateFlags();
    }
    // End methods that handle control events.

    // Implement helper methods.
    private createExampleContacts(): void {
    }

    private checkSelfAdded(): void {
        this.selfAddedFlag = this.contactsData.containsUser(this.currentUserService.user.maxId);
    }

    private packageDataAndUpdateFlags(): void {
        this.FormInstanceElement.textValue = JSON.stringify(this.contactsData);
        this.FormInstanceElement.UserUpdatedData = true;
        this.selfAddedFlag = this.contactsData.containsUser(this.currentUserService.user.maxId);
    }

    private deriveDisplayedColumns(): void {
        this.displayedColumns = ((this.FormField.contactFieldNames != null) && (this.FormField.contactFieldNames.trim() != '') ? this.FormField.contactFieldNames.split('|') : []);
        this.displayedColumns.push('action');
    }

    /**
     * Event which is called when a user chooses an option from the context menu for a grid cell's field.
     * @param eventData - Contains the data associated with the click event that occurred when user chose the context option.
     * @param menuItemName - Contains the context menu item name that was chosen
     */
    public gridCellContextMenuItemClicked(eventData: any, menuItemName: string): void {
        //Get my menu data.
        let menuData: GridCellContextMenuInfo =
            (this.gridCellContextMenu && this.gridCellContextMenu.menuData ?
                <GridCellContextMenuInfo>this.gridCellContextMenu.menuData : null);

        let bSuccess: boolean = false;

        switch (menuItemName) {
            case 'Properties ...':
                {
                    if (menuData && menuData.component) {
                        let hshComponentProperties: any = menuData.component.getProperties();

                        hshComponentProperties['component'] = this;

                        this.onEdit.emit(hshComponentProperties);

                        bSuccess = true;
                    }
                }
                break;

            case 'Delete':
                {
                    //debugger;
                    if (menuData && menuData.columnDef) {
                        let gridConfigColDef: FormField = this.gridConfig.getColumnDefByName(menuData.columnDef.name);

                        if (gridConfigColDef) {
                            bSuccess = this.gridConfig.removeGridColumn(gridConfigColDef.gridColClientId);
                            if (bSuccess) {
                                //***Signal that page isDirty. This will emit to formFieldWrapperComponent
                                this.designChange.emit();
                            }

                        }
                    }
                }
                break;

            default:
                // Unknown menu item.
                break;
        }

        return;
    }
}
